import { useSyncExternalStore } from 'react';

/**
 * Store links + deep-link scheme for the Rovera mobile app.
 *
 * The Play Store URL is real (derived from the published applicationId).
 * The Apple URL needs the numeric App Store Connect ID once the app has a
 * listing — until then this resolves to a clearly-labeled placeholder so it
 * fails loudly (obvious 404) instead of silently pointing somewhere wrong.
 * Override either via env vars once real listings exist.
 */
export const APP_STORE_URL =
  import.meta.env.VITE_APPLE_APP_STORE_URL || 'https://apps.apple.com/app/rovera/id0000000000';
export const PLAY_STORE_URL =
  import.meta.env.VITE_GOOGLE_PLAY_URL ||
  'https://play.google.com/store/apps/details?id=app.rovera.mobile';
/** Custom URL scheme registered in the Flutter app (Info.plist / AndroidManifest). */
export const APP_SCHEME = 'rovera';
/** Canonical smart-link that device-detects and forwards to the right store. */
export const DOWNLOAD_URL = 'https://rovera.app/download';

export type Platform = 'ios' | 'android' | 'desktop';

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export function storeUrlFor(platform: Platform): string {
  return platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
}

/**
 * Best-effort deep link: without Universal Links / App Links configured on
 * the mobile project (no associated domains entitlement yet), a custom
 * scheme + visibility-timeout fallback is the achievable approach — there's
 * no reliable JS signal for "did the app actually open." If the tab is still
 * visible after the timeout, we assume it didn't and send the user to their
 * store instead.
 */
export function attemptAppOpen(path = '', timeoutMs = 1400) {
  const platform = detectPlatform();
  const target = storeUrlFor(platform);
  const deepLink = `${APP_SCHEME}://${path}`;

  let fallbackFired = false;
  const fallback = () => {
    if (fallbackFired || document.hidden) return;
    fallbackFired = true;
    window.location.href = target;
  };

  const timer = window.setTimeout(fallback, timeoutMs);
  const onVisibilityChange = () => {
    if (document.hidden) {
      fallbackFired = true;
      window.clearTimeout(timer);
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange, { once: true });

  window.location.href = deepLink;
}

/* ------------------------------------------------------------------ */
/* Global download-modal store — a single modal instance, triggered     */
/* from anywhere (nav, hero, pricing) without prop-drilling or a        */
/* state-management dependency.                                         */
/* ------------------------------------------------------------------ */

let modalOpen = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function openDownloadModal() {
  modalOpen = true;
  emit();
}
export function closeDownloadModal() {
  modalOpen = false;
  emit();
}
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return modalOpen;
}
export function useDownloadModalOpen() {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Click handler for any "download-intent" CTA: deep-link+fallback on a
 *  phone browser, open the glass modal on desktop. */
export function handleDownloadIntent() {
  if (detectPlatform() === 'desktop') {
    openDownloadModal();
  } else {
    attemptAppOpen();
  }
}
